"use client";

import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { SendHorizonal, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { CustomIcon } from "@/components/custom/icons/custom-icon";
import { Checkbox } from "@/components/ui/checkbox";
import { Typography } from "@/components/custom/typography";
import { useTypedTranslations } from "@/i18n/typed";

interface MessageInputProps {
  onSend: (message: string) => void;
  onFileSelect?: (files: File[]) => void;
  onFileRemove?: (fileIndex: number) => void;
  selectedFiles?: File[];
  isHideUploadFile?: boolean;
  disabledSend?: boolean;
  sendButtonPosition?: string;
  placeholder?: string;
  // optional: notify parent when broadcast toggle changes
  onBroadcastChange?: (value: boolean) => void;
  // optional: extended send that includes broadcast flag
  onSendExtended?: (message: string, broadcastAll: boolean) => void;
  // when true, show the "send all media" checkbox; default hidden
  showSendAllMedia?: boolean;
  // when true, render the message input; when false, hide it
  showMessage?: boolean;
}

export default function MessageInput({
  onSend,
  onFileSelect,
  onFileRemove,
  selectedFiles = [],
  isHideUploadFile,
  disabledSend,
  sendButtonPosition,
  placeholder,
  onBroadcastChange,
  onSendExtended,
  showSendAllMedia = false,
  showMessage = true,
}: MessageInputProps) {
  const t = useTypedTranslations("inquiry");
  const [message, setMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [broadcastAll, setBroadcastAll] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const handleSend = () => {
    // Allow sending when there's a message or when files are attached.
    if (message.trim() === "" && selectedFiles.length === 0) return;
    const trimmed = message.trim();
    if (onSendExtended) {
      onSendExtended(trimmed, broadcastAll);
    } else {
      onSend(trimmed);
    }
    setMessage("");
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0 && onFileSelect) {
      onFileSelect(files);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Disable when sender explicitly disabled, or when there's no message AND no files.
  // If there are selected files, allow sending even with an empty message.
  const isDisabled =
    Boolean(disabledSend) ||
    (message.trim() === "" && selectedFiles.length === 0);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // If caller requested to hide the message input, render nothing.
  // Hooks above must always be called to satisfy React rules.
  if (!showMessage) return null;

  return (
    <div className="rounded-lg relative bg-white flex w-full gap-4">
      <div className="w-full relative">
        <Textarea
          value={message}
          onChange={handleChange}
          placeholder={placeholder || t("messageInput.placeholder")}
          className={cn(
            "min-h-[120px]  rounded-2xl resize-none border-2 border-primary-200 bg-primary-5 focus-visible:ring-0 focus-visible:ring-offset-0 p-3 placeholder:text-base placeholder:text-gray-400",
            disabledSend && "bg-gray-200 text-gray-800",
          )}
          disabled={disabledSend}
          onKeyDown={handleKeyDown}
        />

        {!isHideUploadFile && (
          <div className="flex justify-between items-center absolute bottom-0 w-full  ">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="h-auto hover:bg-transparent bg-transparent"
                onClick={handleFileClick}
              >
                <CustomIcon
                  size={5}
                  name="File"
                  className="text-primary-400 hover:text-primary-600 "
                />
              </Button>
              {selectedFiles.length > 0 && (
                <div className="flex flex-wrap gap-1 max-w-full">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-1 bg-white px-2 py-1 border border-gray-200 rounded-md"
                    >
                      <span className="text-xs text-gray-600 max-w-[100px] truncate">
                        {file.name}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="h-4 w-4 p-0 hover:bg-gray-200 bg-transparent"
                        onClick={() => onFileRemove?.(index)}
                      >
                        <X className="h-3 w-3 text-white rounded-full bg-gray-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              {showSendAllMedia && (
                <div className="flex items-center gap-2 text-sm text-gray-700 px-2.5">
                  <Checkbox
                    checked={broadcastAll}
                    onCheckedChange={(val) => {
                      const checked = Boolean(val);
                      setBroadcastAll(checked);
                      onBroadcastChange?.(checked);
                    }}
                    className="size-4.25"
                  />
                  <Typography variant="detail1" className="select-none">
                    {t("messageInput.sendToAllMedia")}
                  </Typography>
                </div>
              )}

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                className="hidden"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
              />
            </div>
          </div>
        )}
      </div>

      {
        <Button
          type="button"
          size="sm"
          disabled={isDisabled}
          onClick={handleSend}
          className={cn(
            "bg-primary hover:bg-primary-700 rounded w-8 h-8 p-0 flex items-center justify-center disabled:opacity-50",
            sendButtonPosition && `absolute ${sendButtonPosition}`,
            isDisabled && "bg-gray-500",
          )}
        >
          <SendHorizonal className="h-4 w-4" />
        </Button>
      }
    </div>
  );
}
